import { useEffect, useRef, useState, type ReactElement } from 'react'
import { voiceEnglish, type VoiceText } from './voice-text'
const english = (key: VoiceText): string => voiceEnglish[key]

function clock(seconds: number, roundUp = false): string {
  const whole = Math.max(0, roundUp ? Math.ceil(seconds) : Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

export function AudioPlayer({ src, label, durationMs = 0, autoPlay = false, text = english }: {
  src: string; label: string; durationMs?: number; autoPlay?: boolean; text?: (key: VoiceText) => string
}): ReactElement {
  const audio = useRef<HTMLAudioElement>(null)
  const [duration, setDuration] = useState(durationMs / 1000)
  const [position, setPosition] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState(false)
  const [speed, setSpeed] = useState('1')
  useEffect(() => {
    const element = audio.current
    if (!element) return
    if (autoPlay) void element.play().catch((reason: unknown) => {
      // Some browsers need a fresh gesture after the authenticated download.
      if (reason instanceof DOMException && (reason.name === 'NotAllowedError' || reason.name === 'AbortError')) return
      setError(true)
    })
    return () => { element.pause() }
  }, [autoPlay])
  const toggle = async () => {
    const element = audio.current
    if (!element) return
    if (!element.paused) { element.pause(); return }
    if (error) element.load()
    setError(false)
    if (element.ended) element.currentTime = 0
    try { await element.play() }
    catch (reason) { if (!(reason instanceof DOMException && reason.name === 'AbortError')) setError(true) }
  }
  const metadata = () => {
    const value = audio.current?.duration
    if (value && Number.isFinite(value)) setDuration(value)
  }
  return <fieldset className="support-audio-player" aria-label={label}>
    {/* biome-ignore lint/a11y/useMediaCaption: Personal voice recordings have no automatic transcript. */}
    <audio hidden ref={audio} data-support-audio src={src} preload="metadata"
      onLoadedMetadata={metadata} onDurationChange={metadata}
      onTimeUpdate={() => setPosition(audio.current?.currentTime ?? 0)}
      onPlay={() => {
        setPlaying(true)
        for (const other of document.querySelectorAll<HTMLAudioElement>('audio[data-support-audio]')) if (other !== audio.current) other.pause()
      }}
      onPause={() => { setPlaying(false); setWaiting(false) }} onEnded={() => { setPlaying(false); setWaiting(false) }}
      onWaiting={() => setWaiting(true)} onPlaying={() => setWaiting(false)}
      onError={() => { setError(true); setWaiting(false); setPlaying(false) }} />
    <div className="support-audio-main">
      <button type="button" className="support-audio-toggle" aria-label={text(playing ? 'pause' : 'play')} aria-pressed={playing} onClick={() => { void toggle() }}>
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">{playing ? <path d="M6 5h4v14H6zm8 0h4v14h-4Z" /> : <path d="m8 5 11 7-11 7Z" />}</svg>
      </button>
      <div className="support-audio-track">
        <input type="range" min={0} max={duration || 1} step={0.05} value={Math.min(position, duration)} disabled={!duration} dir="ltr"
          aria-label={text('seek')} aria-valuetext={`${clock(position)} / ${clock(duration, true)}`}
          onChange={(event) => { const next = Number(event.target.value); if (audio.current) audio.current.currentTime = next; setPosition(next) }} />
        <div className="support-audio-times" dir="ltr"><span>{clock(position)}</span><span>{clock(duration, true)}</span></div>
      </div>
      <select className="support-audio-speed" aria-label={text('speed')} value={speed} onChange={(event) => {
        setSpeed(event.target.value)
        if (audio.current) {
          audio.current.defaultPlaybackRate = Number(event.target.value)
          audio.current.playbackRate = Number(event.target.value)
        }
      }}>
        {[1, 1.25, 1.5, 2].map((rate) => <option key={rate} value={rate}>{rate}×</option>)}
      </select>
    </div>
    {waiting && <p role="status" className="mt-2 text-xs">{text('loading')}</p>}
    {error && <p role="alert" className="mt-2 text-xs text-danger">{text('loadFailed')}</p>}
  </fieldset>
}
